/* eslint-disable jsdoc/require-jsdoc */
import type { Agent } from '@studio/models/agent';
import { AudioDefaults, toConfiguration, toUri } from '@studio/models/audio';
import type { Audio } from '@studio/models/audio';
import { LocalDefaults } from '@studio/models/audio/local';
import { toStatus } from '@studio/models/device/stream';
import { formatUri, parseUri } from '@studio/models/net';
import { isHydrated, toNumber, toString } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { DeviceOptions, PbDevice, PbStatusDiscovery, PbStudioDevice } from './types.js';
import type { Registrant } from '#~/api/device/registry/index.js';
import type { Discovery$Audio } from '#~/device/discovery/index.js';
import type { Device } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';

/* JS => PB */

export const jsToPbStudioDeviceAudio = (
  device: Device,
  { liveKit }: DeviceOptions
): PbStudioDevice['audio'] => {
  const { audio } = device;

  if (!audio) {
    return undefined;
  }

  return {
    uri: formatUri(toUri(audio.config, { liveKit }))
  };
};

/* PB => JS */

export const pbToJsAudio = (pbDevice: PbDevice): T_Maybe<Audio> => {
  const { pbStatus, pbStudio } = pbDevice;

  const pbStudioAudio = pbStudio?.audio;
  const pbStatusAudio = pbStatus?.audio;

  const config = toConfiguration(parseUri(pbStudioAudio?.uri));

  if (!config) {
    return undefined;
  }

  return defaults(
    { config },
    {
      channels: toNumber(pbStatusAudio?.channels, { default: 1 }),
      error: toString(pbStatusAudio?.status?.error),
      /*  @fixme offset: toNumber(pbStatusAudio?.offset?.value)  */
      status: toStatus(pbStatusAudio?.status?.state)
    },
    AudioDefaults
  );
};

export const pbToJsAudioTemplate = (_registrant: Registrant): Template['audio'] => undefined;

export const pbToJsDiscoveryAudio = (
  agentId: Agent['id'],
  pbDiscovery: PbStatusDiscovery
): T_Maybe<Omit<Discovery$Audio, 'id'>> => {
  const { local } = pbDiscovery;

  if (local) {
    const name = toString(local.name);

    const audio = defaults(
      {
        path: toString(local.id),
        driver: toString(local.api)
      },
      LocalDefaults,
      AudioDefaults
    );

    return name && isHydrated(audio) ? { agentId, audio, name } : undefined;
  }

  return undefined;
};
